import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Client-side mirror of the server's FtpHostAttribute (DataTransformerAPI/Models/Request).
// Keep the two in sync — the server remains the authority.

const HOST_NAME = /^(?=.{1,253}$)[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
const DOTTED_NUMERIC = /^[0-9.]+$/;
const IPV6 = /^[0-9A-Fa-f:.]+$/;

function isIpv4(host: string): boolean {
  const parts = host.split('.');
  return parts.length === 4 && parts.every(p => /^\d{1,3}$/.test(p) && Number(p) <= 255);
}

function isIpv6(host: string): boolean {
  if (!IPV6.test(host) || !host.includes(':')) {
    return false;
  }
  const doubleColons = host.split('::').length - 1;
  if (doubleColons > 1) {
    return false;
  }
  const groups = host.split(':').filter(g => g !== '');
  const maxGroups = doubleColons === 1 ? 7 : 8;
  return groups.length <= maxGroups && groups.every(g => /^[0-9A-Fa-f]{1,4}$/.test(g) || isIpv4(g));
}

/** Returns why `host` is not a valid FTP/SFTP server location, or null when it is. */
export function getFtpHostError(host: string): string | null {
  if (host.includes('://')) {
    return 'Enter the host name only, without a prefix such as ftp:// or sftp://.';
  }
  if (host.includes('/') || host.includes('\\')) {
    return 'Enter the host name only, without a folder path.';
  }
  if (host.includes(':')) {
    return isIpv6(host) ? null : 'Enter the port in the Port field, not in the server location.';
  }
  if (DOTTED_NUMERIC.test(host)) {
    return isIpv4(host) ? null : 'Enter a valid IPv4 address (for example 192.0.2.10).';
  }
  return HOST_NAME.test(host) ? null : 'Enter a valid host name or IP address (for example ftp.example.com).';
}

/** Host name, IPv4 or IPv6 address. Empty values pass — pair with a required validator. */
export const ftpHostValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const host = (control.value ?? '').toString().trim();
  if (!host) {
    return null;
  }
  const error = getFtpHostError(host);
  return error ? { ftpHost: error } : null;
};

/** Required, treating whitespace-only input as empty (matches the server's [Required]). */
export const trimmedRequiredValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  (control.value ?? '').toString().trim() ? null : { required: true };

/** Rejects any whitespace inside the value (surrounding whitespace is trimmed before save). */
export const noInnerWhitespaceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  /\s/.test((control.value ?? '').toString().trim()) ? { whitespace: true } : null;

/** Whole-number TCP port in 1–65535. */
export const portValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const port = Number(value);
  return Number.isInteger(port) && port >= 1 && port <= 65535 ? null : { port: true };
};
